import { inject, Injectable, signal } from '@angular/core';
import { TelegramService } from '@ibc-shared/services/telegram/telegram.service';
import { catchError, combineLatest, EMPTY, filter, interval, take, takeUntil, timer } from 'rxjs';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AppReadinessService {
  private telegramService = inject(TelegramService);
  private userService = inject(UserService);

  isAppReady = signal(false);
  isUserLinked = signal<boolean | null>(null); // null = checking, true = linked, false = not linked
  private isInitializing = false;

  initialize(): void {
    const available = this.telegramService.isAvailable();
    console.log('[ACFE] AppReadinessService.initialize: Telegram available?', available);
    if (!available) {
      console.log('[ACFE] AppReadinessService.initialize: waiting for Telegram...');
      this.waitForTelegramService();
      return;
    }

    console.log('[ACFE] AppReadinessService.initialize: starting Telegram flow');
    this.initializeTelegramFlow();
  }

  private waitForTelegramService(): void {
    const waitTime = 2500;
    const checkInterval = 100;
    console.log(
      '[ACFE] AppReadinessService.waitForTelegramService: polling every',
      checkInterval,
      'ms for',
      waitTime,
      'ms',
    );

    const redirectTimer = timer(waitTime);
    const checkInterval$ = interval(checkInterval).pipe(takeUntil(redirectTimer));

    checkInterval$.subscribe({
      next: () => {
        if (this.telegramService.isAvailable() && !this.isInitializing) {
          console.log(
            '[ACFE] AppReadinessService.waitForTelegramService: Telegram became available',
          );
          this.isInitializing = true;
          this.initializeTelegramFlow();
        }
      },
      complete: () => {
        if (!this.telegramService.isAvailable() && !this.isInitializing) {
          console.log(
            '[ACFE] AppReadinessService.waitForTelegramService: timeout, closing (not in Telegram)',
          );
          this.telegramService.close();
        }
      },
    });
  }

  private initializeTelegramFlow(): void {
    this.isInitializing = true;
    console.log(
      '[ACFE] AppReadinessService.initializeTelegramFlow: waiting for initStatus + initData',
    );

    combineLatest([
      this.telegramService.getInitializationStatus().pipe(
        filter((initialized) => initialized === true),
        take(1),
      ),
      this.telegramService.getInitData$().pipe(
        filter((initData) => initData !== null),
        take(1),
      ),
    ]).subscribe({
      next: () => {
        console.log(
          '[ACFE] AppReadinessService.initializeTelegramFlow: Telegram ready, setting isAppReady=true',
        );
        this.isAppReady.set(true);
        if (this.telegramService.isAvailable()) {
          this.telegramService.expand();
        }
        this.checkUserLinking();
      },
      error: (err) =>
        console.error('[ACFE] AppReadinessService.initializeTelegramFlow: error', err),
      complete: () =>
        console.log('[ACFE] AppReadinessService.initializeTelegramFlow: combineLatest complete'),
    });
  }

  private checkUserLinking(): void {
    this.isUserLinked.set(null);
    console.log('[ACFE] AppReadinessService.checkUserLinking: fetching current user');

    this.userService
      .getCurrentUser()
      .pipe(
        catchError((err) => {
          console.log(
            '[ACFE] AppReadinessService.checkUserLinking: user not linked (error)',
            err?.message ?? err,
          );
          this.isUserLinked.set(false);
          return EMPTY;
        }),
      )
      .subscribe({
        next: (response) => {
          const linked = !!response;
          console.log('[ACFE] AppReadinessService.checkUserLinking: user linked?', linked);
          this.isUserLinked.set(linked);
        },
        error: (err) =>
          console.error('[ACFE] AppReadinessService.checkUserLinking: subscribe error', err),
      });
  }
}
